import React, { useState } from 'react';

import { Link } from 'react-router-dom';

import { projects, currentProjects } from '../data/projects.js';
import { ProjectGrid, ProjectModal, ProjectCard, Modal } from '../sections/ProjectCards';

import Seo from '../ui/Seo';
import Reveal from '../ui/Reveal';
import PageHero from '../sections/PageHero';
import TradeTicker from '../sections/TradeTicker';
import SectionHead from '../sections/SectionHead';
import Bento from '../sections/Bento';
import Stats from '../sections/Stats';
import Cta from '../sections/Cta';
import { site } from '../data/site';

// Headshots are served from public/media/leadership/ rather than imported, so a
// photo can be dropped in without a code change. Until the file exists the card
// falls back to the executive's initials - see the README in that folder.
const photoPath = '/media/leadership/';

const leadership =
[
    {
        id: "yoram-borax",
        name: "Yoram Borax",
        title: "President & Chief Executive Officer",
        photo: `${photoPath}yoram-borax.jpg`,
        summary: "Provides executive leadership across corporate strategy, business development, client relationships, technology integration, and the company’s continued growth.",
        bio:
        [
            "Yoram Borax serves as President and Chief Executive Officer of Smart Electric Solutions, providing executive leadership across corporate strategy, business development, client relationships, technology integration, and the company’s continued growth.",
            <>With more than 20 years of experience spanning technology, electrical systems, and construction, Yoram brings a multidisciplinary perspective to electrical contracting. Throughout his career, he has successfully participated in and completed projects representing more than <strong className="accentColor">$1 billion in underlying real estate value</strong>, providing him with extensive exposure to the technical, financial, and operational requirements of sophisticated construction projects.</>,
            "As President and CEO, Yoram is responsible for establishing the strategic direction of Smart Electric Solutions and ensuring that the company’s resources, personnel, technology, and operating infrastructure remain aligned with its growth objectives.",
            "His background in both construction and technology has also been instrumental in developing the company’s technology-forward operating philosophy. Rather than treating technology as an administrative support function, Smart Electric Solutions integrates it directly into estimating, preconstruction, project management, procurement, field coordination, documentation, reporting, and executive decision-making.",
            "Yoram maintains executive-level involvement with clients and project stakeholders, helping establish clear accountability from preconstruction through project completion. His leadership philosophy emphasizes long-term relationships, transparency, disciplined execution, and the development of systems capable of supporting increasingly large and complex construction programs."
        ],
        responsibilities:
        [
            "Corporate Strategy & Growth", "Executive Client Relations", "Business Development",
            "Strategic Partnerships", "Contract & Commercial Oversight", "Technology Strategy",
            "Organizational Development", "Risk Management", "Executive Project Oversight"
        ]
    },
    {
        id: "mark-petrich",
        name: "Mark Petrich",
        title: "Executive Vice President & Chief Operating Officer",
        photo: `${photoPath}mark-petrich.jpg`,
        summary: "Brings more than 40 years of construction and electrical contracting experience, including executive leadership of an operation that completed more than $1 billion in electrical contracts.",
        bio:
        [
            <>Mark Petrich serves as Executive Vice President and Chief Operating Officer of Smart Electric Solutions and brings more than <strong className="accentColor">40 years of construction and electrical contracting experience</strong> to the organization.</>,
            <>Mark’s career includes executive leadership of a large-scale electrical contracting operation responsible for completing <strong className="accentColor">more than $1 billion in electrical contracts</strong>. This experience provides Smart Electric Solutions with senior operational leadership grounded in the realities of managing substantial electrical construction volume, large field organizations, demanding schedules, complex contractual requirements, and multiple concurrent projects.</>,
            "As COO, Mark is responsible for translating the company’s strategic objectives into repeatable operational execution. His oversight encompasses project operations, manpower planning, field performance, scheduling, procurement strategy, project controls, productivity, quality, safety coordination, and operational accountability.",
            "A central component of Mark’s role is the continued development of the processes and management structure necessary to scale Smart Electric Solutions while maintaining consistent execution standards. This includes establishing standardized project procedures, strengthening communication between field and office teams, monitoring project performance against budgets and schedules, and identifying risks before they impact project outcomes.",
            "His extensive industry experience also provides critical executive oversight during preconstruction and project planning, allowing the company to evaluate constructability, sequencing, labor requirements, procurement challenges, and execution strategy before those issues reach the field."
        ],
        responsibilities:
        [
            "Company Operations", "Project Executive Oversight", "Field Operations",
            "Manpower & Resource Planning", "Project Controls", "Scheduling",
            "Procurement Strategy", "Productivity Management", "Quality Assurance",
            "Operational Risk Management", "Process Standardization"
        ]
    },
    {
        id: "cristhian-vidal",
        name: "Cristhian Vidal",
        title: "Chief Technology Officer",
        photo: `${photoPath}cristhian-vidal.jpg`,
        summary: "Brings more than 15 years of software engineering experience spanning full-stack development, embedded hardware, systems infrastructure, and automation, and leads software, AI, and data across the organization.",
        bio:
        [
            <>Cristhian Vidal serves as Chief Technology Officer of Smart Electric Solutions, bringing more than <strong className="accentColor">15 years of software engineering experience</strong> to an industry undergoing rapid technological transformation.</>,
            "Cristhian’s career spans real-time systems engineering, full-stack web and mobile development, backend services, Linux server administration, network infrastructure, and building automation. He has engineered performance-critical real-time systems, including network communications, database architecture, and performance optimization; led a mobile application development team from design through delivery; and built production web platforms, cross-platform mobile applications, and the servers and networks that run them.",
            "His recent work extends into embedded hardware engineering: bringing up custom electronics built around ARM compute modules, integrating CAN bus networks, analog sensors, power-rail sequencing, battery monitoring, GPS, and LTE cellular connectivity. He builds hardened embedded Linux platforms from the ground up, with cryptographically signed secure boot, hardware security elements, encrypted storage, firewalled networking, and fail-safe over-the-air updates with automatic rollback, engineering that translates directly to connected field equipment, power monitoring, and secure systems deployed on construction sites.",
            "That breadth, from circuit-level hardware integration and low-level systems programming to cloud infrastructure and the applications people use every day, allows Cristhian to design technology that works end to end, from the device in the electrical room to the tablet in the field.",
            "As CTO, Cristhian is responsible for developing and implementing the technology that improves how Smart Electric Solutions estimates, plans, manages, documents, analyzes, and executes its work. His role extends well beyond conventional IT administration: working with executive and operational leadership, he identifies repetitive processes, fragmented information, and operational bottlenecks, and replaces them with purpose-built software, automation, systems integration, data analytics, and artificial intelligence.",
            "Under his leadership, Smart Electric Solutions is building a connected operating environment in which information captured during estimating and preconstruction flows directly into project management, procurement, field operations, cost tracking, change management, and executive reporting, reducing duplicate data entry, improving accuracy, accelerating decisions, and giving management clear visibility into project performance.",
            "Cristhian also leads the evaluation and integration of emerging technologies that improve electrical construction productivity, including AI-assisted document analysis, workflow automation, digital plan management, estimating tools, field reporting systems, dashboards, and mobile workflows. The goal is simple: let project teams spend more time managing construction and less time on repetitive administrative work.",
            "As Smart Electric Solutions grows, Cristhian’s technology leadership provides the infrastructure necessary to scale operations without administrative overhead increasing at the same rate as revenue."
        ],
        responsibilities:
        [
            "Technology Strategy", "Software Engineering", "Systems Integration",
            "Workflow Automation", "Artificial Intelligence", "Data Analytics",
            "Mobile & Field Applications", "Embedded Systems & IoT", "Digital Project Controls",
            "Business Intelligence", "Server & Network Infrastructure", "Cybersecurity",
            "Process Optimization"
        ]
    },
    {
        id: "mario-pinho",
        name: "Mario Pinho",
        title: "Director of Operations",
        photo: `${photoPath}mario-pinho.jpg`,
        summary: "More than 30 years of experience in electrical and general construction, complemented by a background in computer science.",
        bio:
        [
            <>Mario Pinho serves as Director of Operations for Smart Electric Solutions, bringing more than <strong className="accentColor">30 years of experience in electrical construction and general construction</strong>, complemented by a background in computer science.</>,
            "Mario’s combination of field experience, construction knowledge, and technical aptitude provides an important connection between executive management, project management, and field operations.",
            "As Director of Operations, Mario is responsible for supporting the day-to-day execution of projects and ensuring that company standards established at the executive level are consistently implemented in the field. His responsibilities include operational planning, manpower coordination, field supervision, project logistics, schedule coordination, material planning, productivity monitoring, quality control, and communication between project teams.",
            "His computer science background further strengthens Smart Electric Solutions’ ability to implement technology at the operational level. Mario works closely with the company’s technology and operations leadership to ensure that digital systems are practical for field personnel and actually improve project execution rather than creating additional administrative burden.",
            "This combination of construction experience and technological understanding is particularly valuable as Smart Electric Solutions continues to integrate digital workflows into field operations, enabling faster reporting, improved documentation, stronger accountability, and greater visibility into labor and project performance."
        ],
        responsibilities:
        [
            "Field Operations", "Project Execution", "Manpower Coordination",
            "Construction Logistics", "Schedule Coordination", "Productivity",
            "Quality Control", "Material & Equipment Coordination",
            "Field Technology Implementation", "Superintendent & Project Team Support"
        ]
    },
    {
        id: "david-borax",
        name: "David Borax",
        title: "General Manager, Purchasing & Logistics",
        photo: `${photoPath}david-borax.jpg`,
        // Tall portrait - anchor the crop on the face instead of the top edge.
        focus: "center 10%",
        summary: "More than 40 years in electronics, international trade, purchasing, and logistics, including 20+ years as President of businesses exceeding $10 million in annual revenue.",
        bio:
        [
            <>David Borax serves as General Manager of Purchasing and Logistics for Smart Electric Solutions, bringing more than <strong className="accentColor">40 years of experience in electronics, international trade, import/export, retail operations, purchasing, and business management</strong> to the organization.</>,
            <>For more than 20 years, David served as President of businesses generating in excess of <strong className="accentColor">$10 million in annual revenue</strong>, providing him with extensive executive-level experience in purchasing, vendor negotiations, inventory management, logistics, financial controls, international sourcing, and the management of complex day-to-day operations.</>,
            "At Smart Electric Solutions, David is responsible for the purchasing and logistics infrastructure required to support multiple concurrent electrical construction projects. His role extends well beyond the placement of purchase orders. He oversees the flow of materials and equipment from procurement through delivery, working closely with operations and project management to ensure that material availability remains aligned with construction schedules and field requirements.",
            "David’s extensive background in electronics and international import/export provides the company with additional expertise in evaluating suppliers, negotiating purchasing agreements, managing lead times, coordinating freight and logistics, and developing alternative sourcing strategies when conventional supply channels become constrained.",
            "For a large electrical contractor, effective procurement directly impacts project profitability and schedule performance. Material price volatility, equipment lead times, incomplete releases, delayed deliveries, and inefficient purchasing can create significant project risk. David’s responsibility is to establish the controls and supplier relationships necessary to identify and mitigate these risks before they affect field production.",
            "Working alongside executive leadership, project management, and field operations, David helps develop purchasing strategies based on project schedules, material requirements, lead times, pricing opportunities, storage requirements, and anticipated field consumption. This allows Smart Electric Solutions to approach procurement proactively rather than reactively.",
            "Technology also plays an important role in this process. Smart Electric Solutions is integrating purchasing and logistics information with its broader operational systems to provide improved visibility into material releases, purchase orders, committed costs, equipment status, expected delivery dates, inventory, and project requirements. This information enables operations and project teams to identify potential material constraints earlier and make informed decisions before they impact construction.",
            "As the company’s project volume expands, David’s leadership provides Smart Electric Solutions with the purchasing discipline, vendor relationships, and logistics infrastructure necessary to support substantial construction activity while controlling material costs and maintaining reliable supply to the field."
        ],
        responsibilities:
        [
            "Strategic Purchasing", "Procurement Management", "Vendor & Supplier Relations",
            "Contract Negotiation", "Material Logistics", "Inventory Management",
            "International Sourcing", "Import/Export", "Long-Lead Equipment Management",
            "Material Cost Control", "Delivery Coordination", "Supply-Chain Risk Management"
        ]
    }
];

const technologyCapabilities =
[
    {
        title: "Preconstruction & Estimating",
        text: "Digital takeoffs, historical cost intelligence, scope analysis, bid leveling, constructability review, and data-assisted estimating."
    },
    {
        title: "Project Controls",
        text: "Real-time visibility into budgets, committed costs, labor performance, procurement, change orders, schedules, and projected project outcomes."
    },
    {
        title: "Field Productivity",
        text: "Mobile access to drawings, RFIs, submittals, installation information, daily reporting, labor tracking, and field documentation."
    },
    {
        title: "Artificial Intelligence & Automation",
        text: "AI-assisted review of drawings, specifications, contracts, RFIs, submittals, and project documentation, together with automation of repetitive administrative workflows."
    },
    {
        title: "Procurement & Material Management",
        text: "Improved visibility into material requirements, purchasing status, long-lead equipment, deliveries, and potential procurement risks."
    },
    {
        title: "Change Management",
        text: "Structured identification, documentation, pricing, authorization, and tracking of changes to protect both project schedules and commercial interests."
    },
    {
        title: "Executive Reporting",
        text: "Centralized dashboards and performance indicators that provide leadership with visibility across multiple projects and allow management resources to be directed toward issues requiring immediate attention."
    },
    {
        title: "Data & Continuous Improvement",
        text: "Capturing project performance information to improve estimating accuracy, labor forecasting, productivity benchmarks, procurement strategies, and future project execution."
    }
];

const leadershipStructure =
[
    { name: "Yoram Borax",     role: "President & CEO",                          focus: "Corporate Strategy • Client Relationships • Growth • Technology Vision • Executive Oversight" },
    { name: "Mark Petrich",    role: "Executive Vice President & COO",           focus: "Construction Operations • Project Controls • Field Performance • Organizational Scalability" },
    { name: "Cristhian Vidal", role: "Chief Technology Officer",                focus: "Software • Automation • Artificial Intelligence • Data • Systems Integration" },
    { name: "Mario Pinho",     role: "Director of Operations",                   focus: "Field Execution • Manpower • Logistics Coordination • Productivity • Quality Control" },
    { name: "David Borax",     role: "General Manager, Purchasing & Logistics",  focus: "Procurement • Supply Chain • Vendor Management • Material Cost Control • Logistics" }
];

const scaleFigures =
[
    { value: "145+",  label: "Years of combined leadership experience" },
    { value: "$1B+",  label: "In electrical contracting volume across our leadership’s careers" },
    { value: "$1B+",  label: "In real estate project value across our leadership’s careers" }
];

const getInitials = (name) => name.split(' ').map((part) => part[0]).join('');


/* Falls back to initials when the headshot has not been added to
   public/media/leadership/ yet, so a missing file never breaks the page. */
function LeaderPhoto({ leader }) {
    const [failed, setFailed] = useState(!leader.photo);

    if (failed)
        return <div className="leader-initials" aria-hidden="true">{getInitials(leader.name)}</div>;

    return (
        <img
            className="leader-photo"
            src={leader.photo}
            alt={`${leader.name}, ${leader.title}`}
            style={leader.focus ? { objectPosition: leader.focus } : undefined}
            loading="lazy"
            decoding="async"
            onError={() => setFailed(true)}
        />
    );
}

function LeaderModal({ leader, onClose }) {
    return (
        <Modal
            title={leader.name}
            subtitle={leader.title}
            closeLabel="Close biography"
            labelledBy="leaderModalName"
            onClose={onClose}
        >
            <div className="modal__split">
                <LeaderPhoto leader={leader} />

                <div className="modal__body">
                    {leader.bio.map((paragraph, index) => (
                        <p key={index}>{paragraph}</p>
                    ))}

                    <span className="modal__label">Executive Responsibilities</span>
                    <div>
                        {leader.responsibilities.map(responsibility => (
                            <span key={responsibility} className="about-chip">{responsibility}</span>
                        ))}
                    </div>
                </div>
            </div>
        </Modal>
    );
}

/* The About page shows a short preview of the landmark work; the full,
   category-split list lives on the Portfolio page. */
const PROJECTS_PREVIEW_COUNT = 8;

export default function About() {
    const [selectedLeader, setSelectedLeader] = useState(null);
    const [selectedProject, setSelectedProject] = useState(null);

    const totalUnits = projects.reduce((sum, item) => sum + (item.units || 0), 0);
    const previewProjects = projects.slice(0, PROJECTS_PREVIEW_COUNT);

    return (
        <>
            <Seo
                title="Executive Leadership"
                description="Meet the executive leadership of Smart Electric Solutions: a team combining more than 145 years of experience across electrical contracting, construction, technology, operations, procurement, and logistics."
                keywords="Smart Electric Solutions leadership, executive team, electrical contractor management, construction technology, preconstruction, project controls, procurement, electrical construction Florida"
                path="/about"
                schema={{
                    '@type': 'AboutPage',
                    name: 'Executive Leadership',
                    description: 'Meet the executive leadership of Smart Electric Solutions.',
                    url: `${site.origin}/about`,
                    mainEntity: {
                        '@type': 'Organization',
                        name: site.name,
                        url: site.origin,
                        telephone: '+1-305-205-6409',
                        address: {
                            '@type': 'PostalAddress',
                            streetAddress: '3040 N 29th Ave #1',
                            addressLocality: 'Hollywood',
                            addressRegion: 'FL',
                            postalCode: '33020',
                            addressCountry: 'US'
                        },
                        employee: leadership.map(leader => ({
                            '@type': 'Person',
                            name: leader.name,
                            jobTitle: leader.title
                        }))
                    }
                }}
            />

            {selectedLeader && (
                <LeaderModal leader={selectedLeader} onClose={() => setSelectedLeader(null)} />
            )}

            {selectedProject && (
                <ProjectModal project={selectedProject} onClose={() => setSelectedProject(null)} />
            )}

            <PageHero
                eyebrow="About"
                title="Executive Leadership"
                lead="A multidisciplinary team combining decades of electrical construction experience, large-scale project leadership, technology expertise, and operational management."
                crumbs={[{ label: 'About' }]}
            />

            <TradeTicker />

            <section className="section">
                <div className="shell">
                    <Reveal className="prose" y={26}>
                        <p>Smart Electric Solutions is led by a multidisciplinary executive team combining decades of electrical construction experience, large-scale project leadership, technology expertise, and operational management. Our leadership structure is designed around the requirements of complex, high-value construction programs: disciplined preconstruction, rigorous project controls, field execution, financial accountability, schedule management, quality assurance, safety, and technology-enabled operations.</p>
                        <p>Collectively, our leadership brings experience associated with billions of dollars in real estate development and electrical contracting activity. This depth of experience, coupled with a technology-forward operating model, enables Smart Electric Solutions to pursue and execute projects of significant size and complexity while maintaining the responsiveness and accountability of an owner-led organization.</p>
                        <p>Our objective is not simply to perform electrical construction. We are building a scalable electrical contracting platform capable of supporting sophisticated developers, general contractors, construction managers, institutional clients, and owners across multiple concurrent projects.</p>
                    </Reveal>

                    <ul className="cards" style={{ '--cols': 3, marginTop: 'clamp(2.5rem, 6vw, 4rem)' }}>
                        {leadership.map((leader, i) => (
                            <Reveal as="li" key={leader.id} delay={(i % 3) * 0.08} y={26}>
                                <button
                                    type="button"
                                    className="leader"
                                    onClick={() => setSelectedLeader(leader)}
                                    aria-label={`Read the full biography of ${leader.name}`}
                                >
                                    <LeaderPhoto leader={leader} />

                                    <div className="leader__body">
                                        <h3 className="leader__name">{leader.name}</h3>
                                        <p className="leader-role">{leader.title}</p>
                                        <p className="leader__summary">{leader.summary}</p>
                                        <span className="leader-readmore">READ FULL BIO →</span>
                                    </div>
                                </button>
                            </Reveal>
                        ))}
                    </ul>
                </div>
            </section>

            <section className="section section--ruled">
                <div className="shell">
                    <SectionHead
                        eyebrow="Live work"
                        title="Currently Underway"
                        lead="Work in progress under Smart Electric Solutions today."
                    />

                    <ul className="cards" style={{ '--cols': 2, marginTop: 'clamp(2.5rem, 6vw, 4rem)' }}>
                        {currentProjects.map((item, i) => (
                            <Reveal as="li" key={item.id} delay={(i % 2) * 0.08} y={26}>
                                <ProjectCard project={item} onSelect={setSelectedProject} />
                            </Reveal>
                        ))}
                    </ul>
                </div>
            </section>

            <section className="section section--raised">
                <div className="shell">
                    <SectionHead
                        eyebrow="Track record"
                        title="Landmark Project Experience"
                    />

                    <Reveal className="prose" y={24} delay={0.1}>
                        <p>Our executive leadership has delivered the electrical scope on some of South Florida’s most recognizable buildings (oceanfront luxury towers, flagship hotels, mixed-use districts, institutional facilities and signature restaurants), working alongside general contractors including Coastal Construction, Suffolk Construction, John Moriarty &amp; Associates, Moss, Plaza and Beauchamp, for developers including The Related Group, Dezer Development, Property Markets Group, Fortune International and Chateau Group.</p>
                        <p>The projects below represent work completed under that leadership prior to the founding of Smart Electric Solutions. They reflect the scale, complexity and coordination our team is accustomed to managing. A selection appears here; the full portfolio, split into residential and commercial work, is on the <Link to="/portfolio">portfolio page</Link>.</p>
                    </Reveal>

                    <div style={{ margin: 'clamp(2.5rem, 6vw, 4rem) 0' }}>
                        <Stats items={[
                            { value: '$600M+', label: 'In completed project value, last ten years' },
                            { value: String(projects.length), label: 'Landmark projects delivered' },
                            { value: totalUnits.toLocaleString(), label: 'Residential and hospitality units' }
                        ]} />
                    </div>

                    <ProjectGrid items={previewProjects} onSelect={setSelectedProject} />

                    <Reveal delay={0.1} style={{ textAlign: 'center', marginTop: 'clamp(2.5rem, 5vw, 3.5rem)' }}>
                        <Link className="btn btn--accent" to="/portfolio">
                            VIEW ALL {projects.length} PROJECTS
                            <span className="btn__arrow" aria-hidden="true">→</span>
                        </Link>
                    </Reveal>
                </div>
            </section>

            <Bento
                eyebrow="How we operate"
                title="Technology-Enabled Electrical Construction"
                lead="The next generation of large electrical contractors will be defined not only by their ability to deploy labor and material, but by their ability to manage information, predict risk, measure productivity, and make decisions faster than traditional contractors."
                items={technologyCapabilities.map(item => ({ title: item.title, body: item.text }))}
                cols={4}
                outro="Technology is integrated into our operating model rather than treated as a separate department. The result is an organization designed to operate with greater visibility, consistency, and accountability as project volume increases."
            />

            <section className="section section--ruled">
                <div className="shell">
                    <SectionHead eyebrow="Structure" title="Built to Scale" />

                    <div style={{ margin: 'clamp(2.5rem, 6vw, 4rem) 0' }}>
                        <Stats items={scaleFigures} />
                    </div>

                    <Reveal className="prose" y={24}>
                        <p>Smart Electric Solutions’ organizational structure is intentionally designed around scalability.</p>
                        <p>Our leadership team represents <strong>more than 145 years of combined experience</strong> across electrical contracting, construction, technology, software engineering, operations, procurement, logistics, international trade, and business management, including leadership experience associated with more than <strong>$1 billion in electrical contracting volume and more than $1 billion in real estate project value</strong>.</p>
                        <p>Rather than relying exclusively on additional personnel to support increasing revenue, we focus on developing repeatable processes, standardized operating procedures, technology-enabled workflows, measurable performance indicators, and clearly defined accountability throughout the organization.</p>
                        <p>This allows Smart Electric Solutions to expand project capacity while maintaining executive visibility into the factors that ultimately determine project success: <strong>safety, manpower, productivity, procurement, schedule, quality, cost, documentation, cash flow, and client satisfaction.</strong></p>
                        <p>The organization is structured so that each major component of electrical construction has dedicated senior leadership:</p>
                    </Reveal>

                    <ul className="roster" style={{ marginTop: 'clamp(2rem, 4vw, 3rem)' }}>
                        {leadershipStructure.map((entry, i) => (
                            <Reveal as="li" key={entry.name} delay={(i % 2) * 0.08} y={22}>
                                <h3>{entry.name}</h3>
                                <p className="leader-role">{entry.role}</p>
                                <p>{entry.focus}</p>
                            </Reveal>
                        ))}
                    </ul>

                    <Reveal className="prose" y={24} delay={0.1} style={{ marginTop: 'clamp(2rem, 4vw, 3rem)' }}>
                        <p>This structure creates an important separation of responsibilities while maintaining centralized executive visibility. Project management and field leadership can concentrate on <strong>building the work</strong>, while dedicated procurement and logistics leadership concentrates on ensuring that <strong>the people building the work have the right material, equipment, information, and resources when they need them.</strong></p>
                        <p>At the same time, the technology organization connects these functions through data and automation, giving executive management visibility across <strong>estimating, labor, purchasing, material commitments, project costs, scheduling, field productivity, and overall project performance.</strong></p>
                    </Reveal>
                </div>
            </section>

            <section className="section section--raised">
                <div className="shell">
                    <SectionHead eyebrow="Philosophy" title="Our Operating Philosophy" />

                    <Reveal className="prose" y={24} delay={0.1}>
                        <p>Smart Electric Solutions is building an electrical contracting organization around a simple principle:</p>

                        <p className="about-pullquote">Experienced people. Better information. Disciplined execution.</p>

                        <p>We combine the institutional knowledge of seasoned electrical construction executives with modern software, automation, data, and artificial intelligence to create a contractor capable of responding quickly without sacrificing the controls required for large-scale construction.</p>
                        <p>Our goal is to provide clients with more than electrical installation. We provide a sophisticated construction partner capable of participating early in preconstruction, identifying risk, coordinating complex systems, managing significant manpower and procurement requirements, providing transparent project information, and delivering predictable results.</p>
                        <p>This combination of <strong>executive experience, field expertise, operational discipline, and technology-driven execution</strong> positions Smart Electric Solutions to serve as a trusted electrical contracting partner for major residential, multifamily, hospitality, commercial, mixed-use, and other complex construction programs.</p>
                    </Reveal>
                </div>
            </section>

            <Cta
                eyebrow="Work with us"
                title="Bring us in early."
                lead="The earlier we are involved, the more risk comes off the table. Tell us about the programme and we will tell you what we would do differently."
            />
        </>
    );
}
